import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { SHOPIFY_PRODUCT_TAG, SHOPIFY_PRODUCT_NAME } from '../../config';
import { findProductVariant } from '../../utils';
import { useConfiguratorDispatch, useRootSelector } from '../../store';
import {
  EditSessionLinkedUpsell,
  selectAllConfigSnapshots,
  selectEditSessionMultiConfigSessionId,
  selectEditSessionShopifyKeysByConfigId,
  selectEditSessionUpsellsByConfigId,
  selectSubmittedCartItemsByConfigId,
  setSubmittedCartItems
} from '../multiConfig';

import {
  selectCartItem,
  selectItemProperties,
  selectSlots
} from '../item/store/itemSlice';
import { useFontsAndFields } from '../fonts/TextAndFont.hooks';
import { useIconsAndLabels } from '../iconAndLabel/IconAndLabelHooks';
import { useMaterials } from '../clothingType/Materials.hooks';
import { useGetProductQuery } from '../api';
import { useLabelShape } from '../LabelShapes/LabelShapeHooks';

import {
  addShopifyCartItem,
  addShopifyCartItems,
  buildCartItemProperties,
  buildPrimaryApiCartItem,
  buildUpsellApiCartItem,
  createMultiConfigSession,
  findMultiConfigAddonVariant,
  findMultiConfigIncludedVariant,
  getApiCartItem,
  getQuantityProperty,
  removeShopifyCartItem,
  resolveConfigVariant,
  sendApiCartItem,
  updateApiCartItem,
  updateShopifyCartItem
} from './utils';
import { UpsellProduct } from '../upsells/types';
import { LabelSlot, Material } from '../../types';

// A configuration's own Shopify line properties include upsell-irrelevant
// keys (its own quantity/shape/material identity) alongside personalization
// (name/style/color/font/icon) that a linked upsell should mirror. Keeping
// an upsell's own identity untouched while syncing personalization matches
// how the single-item edit flow already keeps a regular product's upsells
// in sync when its base item is edited.
const UPSELL_EXCLUDED_PROPERTY_KEYS = new Set([
  'Labels',
  'Shape',
  'Clothing Type',
  '_material_id',
  '_product_id',
  '_cart_item_id',
  '_order_system',
  '_parent_item_id',
  '_multi_config_session_id',
  '_multi_config_index',
  '_multi_config_count'
]);

function buildSyncedUpsellShopifyProperties(
  baseProperties: Record<string, string | undefined>,
  existingUpsellProperties: Record<string, string>
): Record<string, string> {
  const merged: Record<string, string> = { ...existingUpsellProperties };
  for (const [key, value] of Object.entries(baseProperties)) {
    if (UPSELL_EXCLUDED_PROPERTY_KEYS.has(key) || value === undefined) continue;
    merged[key] = value;
  }
  return merged;
}

// A two-line text field (e.g. first/last name) is stored as one Redux value
// joined with a literal `\n` (see TextInputs.tsx) and split back into its
// two Shopify property keys for a configuration's own cart line
// (buildCartLineItemProperties in itemSlice.ts). Upsell/addon line items
// build their properties by hand instead of going through that function, so
// without this same split an addon's Name property would carry the raw,
// still-joined value - a literal embedded newline - straight into Shopify's
// cart data.
function assignTextFieldProperty(
  target: Record<string, string>,
  field: { display_name?: string; display_name_2?: string | null; value: string }
) {
  const { display_name, display_name_2, value } = field;
  if (display_name_2) {
    const textLines = value.trimEnd().split('\n');
    target[display_name!] = textLines[0];
    target[display_name_2] = textLines[1];
  } else {
    target[display_name!] = value;
  }
}

// Syncs every upsell linked to a configuration (see reconstructMultiConfigSessionForEdit)
// so its personalization stays in step with that configuration's current
// values - both its Shopify line properties and its own customer-api cart
// item record (style_id/palette_id/font_id/icon_id/text_fields/previewSvg),
// preserving everything else about its stored record (product_id/label_slots/
// etc, by fetching and spreading over its current data rather than
// reconstructing it from scratch).
async function syncLinkedUpsells(
  configId: string,
  linkedUpsells: EditSessionLinkedUpsell[],
  baseApiCartItemFields: {
    style_id: string | null;
    palette_id: string | null;
    font_id: string | null;
    icon_id: string | null;
    text_fields: Array<{ text_field_id: string; value: string }>;
  },
  baseShopifyLineProperties: Record<string, string | undefined>
): Promise<void> {
  await Promise.all(
    linkedUpsells.map(async upsell => {
      const syncedProperties = buildSyncedUpsellShopifyProperties(
        baseShopifyLineProperties,
        upsell.properties
      );
      await updateShopifyCartItem(upsell.key, {
        quantity: 1,
        properties: syncedProperties
      });

      const existingUpsellCartItem = await getApiCartItem(upsell.cartItemId);

      // The upsell's own personalized preview is a separate off-screen node
      // (HiddenConfigPreviews renders one per configuration x available
      // upsell, keyed by the upsell's own product_id - stored on its
      // customer-api record, since it's never sent as a Shopify line
      // property). Re-capture it so it reflects the configuration's new
      // values too, rather than leaving the stale, pre-edit image in place.
      const previewNode = document.getElementById(
        `config-preview-${configId}-upsell-${existingUpsellCartItem.product_id}`
      );
      if (previewNode) {
        await waitForPreviewToSettle(previewNode);
      }
      const previewSvg =
        previewNode?.outerHTML || existingUpsellCartItem.previewSvg;

      await updateApiCartItem(upsell.cartItemId, {
        ...existingUpsellCartItem,
        product_id: existingUpsellCartItem.product_id,
        legacy_product_code: existingUpsellCartItem.legacy_product_code ?? '',
        previewSvg: previewSvg ?? '',
        style_id: baseApiCartItemFields.style_id,
        palette_id: baseApiCartItemFields.palette_id,
        font_id: baseApiCartItemFields.font_id,
        icon_id: baseApiCartItemFields.icon_id,
        text_fields: baseApiCartItemFields.text_fields
      });
    })
  );
}

// The shared preview <svg> renders its personalized content (name text,
// icon, style layers) via an async effect in VariantPreviewComponent - it
// fetches font/icon assets, then swaps in the rendered markup once that
// resolves. A synchronous flushSync only guarantees React has re-rendered
// with the new configuration's props; it says nothing about that async work
// having finished. So instead of reading the DOM immediately, wait for its
// mutations to quiet down (indicating the async render settled), with a
// hard timeout as a safety net in case something never resolves.
function waitForPreviewToSettle(
  node: Element,
  quietMs = 150,
  timeoutMs = 5000
): Promise<void> {
  return new Promise(resolve => {
    let done = false;
    let settledTimer: ReturnType<typeof setTimeout>;

    const finish = () => {
      if (done) return;
      done = true;
      clearTimeout(settledTimer);
      clearTimeout(hardTimeout);
      observer.disconnect();
      resolve();
    };

    const observer = new MutationObserver(() => {
      clearTimeout(settledTimer);
      settledTimer = setTimeout(finish, quietMs);
    });

    observer.observe(node, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true
    });

    // Also start the quiet timer immediately, in case switching produced no
    // mutation at all (e.g. the target configuration renders identically to
    // what's already on screen).
    settledTimer = setTimeout(finish, quietMs);
    const hardTimeout = setTimeout(finish, timeoutMs);
  });
}

// Reads every configuration's own rendered preview <svg> from the
// off-screen nodes rendered by HiddenConfigPreviews (one per configuration,
// each driven by that configuration's own properties via
// itemPropertiesOverride - see SharedPreview.tsx) - `previewElementId` maps
// a configuration id to that specific node's id. Waits in parallel (there's
// no shared state to serialize on anymore) rather than switching the
// visibly-active configuration to render each one in turn, which used to
// cause every preview on screen to visibly flash through each
// configuration.
async function capturePreviewSvgPerConfig(
  configSnapshots: Array<{ id: string }>,
  previewElementId: (configId: string) => string
): Promise<Record<string, string>> {
  const entries = await Promise.all(
    configSnapshots.map(async snapshot => {
      const node = document.getElementById(previewElementId(snapshot.id));
      if (node) {
        await waitForPreviewToSettle(node);
      }
      return [snapshot.id, node?.outerHTML || ''] as const;
    })
  );

  return Object.fromEntries(entries);
}

export function useAddBaseProductToCart(
  productTag?: string,
  cartItemId?: string
) {
  const properties = useSelector(selectItemProperties);
  const primaryCartItemProperties = useSelector(selectCartItem);
  const { fonts, hideFontsAndFields } = useFontsAndFields();
  const { showIcons, labelHasMultipleVariants } = useIconsAndLabels();
  const { showLabelShapes } = useLabelShape();
  const { materials } = useMaterials();
  const { data: apiProduct } = useGetProductQuery(
    productTag || SHOPIFY_PRODUCT_TAG
  );
  const slots = useRootSelector(selectSlots);

  const addBaseProduct = useCallback(async (): Promise<{
    success: boolean;
    productName?: string;
    cartItemId?: string;
    error?: string;
  }> => {
    if (!apiProduct) {
      return { success: false, error: 'Product not loaded' };
    }

    const primaryApiCartItem = buildPrimaryApiCartItem(properties, apiProduct);

    let primaryCartItemId;
    try {
      if (cartItemId) {
        primaryCartItemId = await updateApiCartItem(
          cartItemId,
          primaryApiCartItem
        );
      } else {
        primaryCartItemId = await sendApiCartItem(primaryApiCartItem);
      }
    } catch (error) {
      console.error('Error adding item to API cart:', error);
      return { success: false, error: 'Error adding to cart' };
    }

    // Build Shopify cart properties
    const {
      Font,
      ['Clothing Type']: clothingType,
      Icon,
      Style,
      Palette,
      ...otherPrimaryCartItemProperties
    } = primaryCartItemProperties;

    const fieldNames: { [key: string]: string | undefined } = {};

    const hasNameField = otherPrimaryCartItemProperties.Name;
    if (hasNameField) {
      fieldNames.Name = otherPrimaryCartItemProperties.Name;
      delete otherPrimaryCartItemProperties.Name;
    }

    const hasFullNameField = otherPrimaryCartItemProperties['Full Name'];
    if (hasFullNameField) {
      fieldNames['Full Name'] = otherPrimaryCartItemProperties['Full Name'];
      delete otherPrimaryCartItemProperties['Full Name'];
    }

    if (!hideFontsAndFields) {
      Object.entries(otherPrimaryCartItemProperties).forEach(([key, value]) => {
        if (!key.startsWith('_')) {
          fieldNames[key] = value;
          delete otherPrimaryCartItemProperties[key];
        }
      });
    }

    const userProperties: { [key: string]: string | undefined } = {
      Style,
      Color: Palette
    };

    if (fonts?.length > 1) {
      userProperties.Font = Font;
    }

    if (showIcons) {
      userProperties.Icon = Icon;
    }

    const shapes = [];
    if (showLabelShapes && properties.labelShape?.display_name) {
      shapes.push(properties.labelShape.display_name);
    }
    apiProduct?.label_slots?.forEach(labelSlot => {
      const labelId = slots?.[labelSlot.product_label_slot_id]?.labelId;
      const label = apiProduct.labels.find(l => l.label_id === labelId);
      const isIterative = label?.iterative_style_ids?.includes(
        properties?.style?.style_id ?? ''
      );
      const hasMultipleVariants = labelHasMultipleVariants[labelId || ''];

      if (hasMultipleVariants && !isIterative) {
        const variantId =
          properties.slots[labelSlot.product_label_slot_id].variantId;
        const variant = label?.variants.find(
          v => v.label_variant_id === variantId
        );
        if (variant?.system_name) shapes.push(variant.system_name);
      }
    });

    if (shapes.length) {
      userProperties.Shape = shapes.join(', ');
    }

    if (materials?.length > 1) {
      userProperties['Clothing Type'] = clothingType;
    }

    const quantity = getQuantityProperty(properties.slots, apiProduct);

    const formattedItemProperties = {
      Labels: quantity.toString(),
      ...fieldNames,
      ...userProperties,
      ...otherPrimaryCartItemProperties,
      _order_system: 'NEW',
      _cart_item_id: primaryCartItemId,
      _product_id: apiProduct?.product_id,
      _style_id: properties.style?.style_id,
      _palette_id: properties.palette?.palette_id,
      _font_id: properties.font?.font_id,
      _icon_id: properties.icon?.icon_id,
      _material_id: properties.clothingType?.material_id
    };

    const { labelShape, palette, style } = properties;
    const productVariant = findProductVariant(labelShape, palette, style);

    const primaryProductItem = {
      id: productVariant?.id,
      quantity: 1,
      properties: formattedItemProperties
    };

    if (cartItemId) {
      const params = new URLSearchParams(window.location.search);
      const key = params.get('key')!;
      const [variantId, _hash] = key.split(':');

      if (Number(variantId) !== primaryProductItem.id) {
        await removeShopifyCartItem(key);
        await addShopifyCartItem(primaryProductItem);
      } else {
        await updateShopifyCartItem(key, primaryProductItem);
      }
    } else {
      const response = await addShopifyCartItem(primaryProductItem);

      if (!response.ok) {
        const json = await response.json();
        console.error('error adding to cart', json);
        return { success: false, error: 'Error adding to cart' };
      }
    }

    const productName = apiProduct.display_name || SHOPIFY_PRODUCT_NAME;
    return { success: true, productName, cartItemId: primaryCartItemId };
  }, [
    properties,
    apiProduct,
    primaryCartItemProperties,
    hideFontsAndFields,
    fonts?.length,
    showIcons,
    showLabelShapes,
    materials?.length,
    cartItemId,
    slots,
    labelHasMultipleVariants
  ]);

  return addBaseProduct;
}

// Submits an entire multi-config session (one cart line + one customer-api
// cart item per configuration, all sharing one multi_config_session_id) -
// used both by AddToCartButton directly (no upsells) and by
// ShowUpsellsButton (product has upsells, tied to configuration #1 - see
// [[project_multi_config_addon_linking]] memory for the SKU-based addon
// pricing this mirrors). Returned shape matches useAddBaseProductToCart's so
// both callers can treat them interchangeably; `cartItemId` on success is
// configuration #1's cart_item_id, for upsell _parent_item_id linking.
// `configCartItems` carries every configuration's own cart_item_id, so an
// upsell chosen afterwards can be added once per configuration rather than
// just to configuration #1.
export function useSendMultiConfigSessionToCart(productTag?: string) {
  const dispatch = useConfiguratorDispatch();
  const { fonts, hideFontsAndFields } = useFontsAndFields();
  const { showIcons, labelHasMultipleVariants } = useIconsAndLabels();
  const { showLabelShapes } = useLabelShape();
  const { materials } = useMaterials();
  const { data: apiProduct } = useGetProductQuery(
    productTag || SHOPIFY_PRODUCT_TAG
  );
  const configSnapshots = useRootSelector(selectAllConfigSnapshots);

  const sendMultiConfigSession = useCallback(async (): Promise<{
    success: boolean;
    productName?: string;
    cartItemId?: string;
    configCartItems?: Array<{ configId: string; cartItemId: string }>;
    error?: string;
  }> => {
    if (!apiProduct) {
      return { success: false, error: 'Product not loaded' };
    }

    const addonVariant = findMultiConfigAddonVariant(
      apiProduct.multi_config_addon_sku
    );
    const includedVariant = findMultiConfigIncludedVariant(
      apiProduct.multi_config_included_sku
    );
    const pricingThreshold = apiProduct.multi_config_pricing_threshold ?? 2;
    const needsIncludedVariant = configSnapshots.some(
      (_, index) => index > 0 && index + 1 < pricingThreshold
    );
    const needsAddonVariant = configSnapshots.some(
      (_, index) => index > 0 && index + 1 >= pricingThreshold
    );

    // Blocking the whole session (not just the affected configurations) is
    // deliberate: there is no safe partial submission here, and falling back
    // to the full-price variant for the addon/included-priced configurations
    // would silently undercharge. Configuration #1 alone can still be added
    // by removing the extra configurations first.
    if (needsIncludedVariant && !includedVariant) {
      return {
        success: false,
        error:
          'Included configuration unavailable - remove extra configs to continue'
      };
    }
    if (needsAddonVariant && !addonVariant) {
      return {
        success: false,
        error: 'Addon unavailable - remove extra configs to continue'
      };
    }

    let sessionId: string;
    try {
      sessionId = await createMultiConfigSession(apiProduct.product_id);
    } catch (error) {
      return { success: false, error: 'Error adding to cart' };
    }

    const previewSvgByConfigId = await capturePreviewSvgPerConfig(
      configSnapshots,
      configId => `config-preview-${configId}`
    );

    // Promise.allSettled (not Promise.all) so one configuration's failure
    // doesn't just produce a generic error - it lets us name exactly which
    // configuration(s) had trouble. The submission as a whole stays
    // all-or-nothing though: if any configuration failed to save, none are
    // sent to Shopify, since a partial cart would silently diverge from
    // what the shopper saw on screen (e.g. missing one kid's labels).
    const results = await Promise.allSettled(
      configSnapshots.map(async (snapshot, index) => {
        const apiCartItem = {
          ...buildPrimaryApiCartItem(
            snapshot.properties,
            apiProduct,
            previewSvgByConfigId[snapshot.id]
          ),
          multi_config_session_id: sessionId
        };
        const cartItemId = await sendApiCartItem(apiCartItem);

        const variantId = resolveConfigVariant(
          index,
          snapshot.properties,
          apiProduct,
          addonVariant,
          includedVariant
        );

        if (!variantId) {
          throw new Error(`No variant found for configuration ${index + 1}`);
        }

        const itemProperties = {
          ...buildCartItemProperties(snapshot.properties, {
            apiProduct,
            cartItemId,
            fonts,
            showIcons,
            showLabelShapes,
            materials,
            hideFontsAndFields,
            labelHasMultipleVariants
          }),
          _multi_config_session_id: sessionId,
          _multi_config_index: (index + 1).toString(),
          _multi_config_count: configSnapshots.length.toString()
        };

        return {
          configId: snapshot.id,
          cartItemId,
          shopifyLine: {
            id: variantId,
            quantity: 1,
            properties: itemProperties
          }
        };
      })
    );

    const failedConfigNumbers = results
      .map((result, index) => (result.status === 'rejected' ? index + 1 : null))
      .filter((n): n is number => n !== null);

    if (failedConfigNumbers.length > 0) {
      console.error(
        'error building multi-config session cart items',
        results
          .filter(
            (result): result is PromiseRejectedResult =>
              result.status === 'rejected'
          )
          .map(result => result.reason)
      );
      return {
        success: false,
        error: `Error saving configuration${
          failedConfigNumbers.length > 1 ? 's' : ''
        } ${failedConfigNumbers.join(', ')} - please try again`
      };
    }

    const fulfilledResults = results.filter(
      (result): result is (typeof results)[number] & { status: 'fulfilled' } =>
        result.status === 'fulfilled'
    );

    const configCartItems = fulfilledResults.map(({ value }) => ({
      configId: value.configId,
      cartItemId: value.cartItemId
    }));
    const primaryCartItemId = fulfilledResults[0]?.value.cartItemId;
    const shopifyLines = fulfilledResults.map(({ value }) => value.shopifyLine);

    const response = await addShopifyCartItems(shopifyLines);

    if (!response.ok) {
      const json = await response.json();
      console.error('error adding multi-config session to cart', json);
      return { success: false, error: 'Error adding to cart' };
    }

    dispatch(
      setSubmittedCartItems(
        Object.fromEntries(
          configCartItems.map(({ configId, cartItemId }) => [
            configId,
            cartItemId
          ])
        )
      )
    );

    const productName = apiProduct.display_name || SHOPIFY_PRODUCT_NAME;
    return {
      success: true,
      productName,
      cartItemId: primaryCartItemId,
      configCartItems
    };
  }, [
    apiProduct,
    configSnapshots,
    dispatch,
    fonts,
    showIcons,
    showLabelShapes,
    materials,
    hideFontsAndFields,
    labelHasMultipleVariants
  ]);

  return sendMultiConfigSession;
}

// Re-saves an existing multi-config session that's currently being edited
// (see reconstructMultiConfigSessionForEdit / selectIsEditingMultiConfigSession).
// Unlike a fresh session, this must update existing cart items/Shopify lines
// in place where possible, create new ones for configurations added during
// the edit (via "Configure Another"), and remove Shopify lines for
// configurations deleted during the edit. Pricing stays positional (index 0
// = full price) exactly like a fresh session - a configuration's position
// can change during editing (e.g. the original #1 was deleted), and pricing
// has always been decided by array order at save time, not a fixed identity
// (see useSendMultiConfigSessionToCart).
export function useSaveMultiConfigSessionEdit(productTag?: string) {
  const dispatch = useConfiguratorDispatch();
  const configSnapshots = useRootSelector(selectAllConfigSnapshots);
  const submittedCartItemsByConfigId = useRootSelector(
    selectSubmittedCartItemsByConfigId
  );
  const editSessionShopifyKeysByConfigId = useRootSelector(
    selectEditSessionShopifyKeysByConfigId
  );
  const editSessionMultiConfigSessionId = useRootSelector(
    selectEditSessionMultiConfigSessionId
  );
  const editSessionUpsellsByConfigId = useRootSelector(
    selectEditSessionUpsellsByConfigId
  );
  const { fonts, hideFontsAndFields } = useFontsAndFields();
  const { showIcons, labelHasMultipleVariants } = useIconsAndLabels();
  const { showLabelShapes } = useLabelShape();
  const { materials } = useMaterials();
  const { data: apiProduct } = useGetProductQuery(
    productTag || SHOPIFY_PRODUCT_TAG
  );

  const saveSessionEdit = useCallback(async (): Promise<{
    success: boolean;
    productName?: string;
    cartItemId?: string;
    error?: string;
  }> => {
    if (!apiProduct) {
      return { success: false, error: 'Product not loaded' };
    }
    if (!editSessionMultiConfigSessionId) {
      return { success: false, error: 'Error saving - missing session' };
    }

    const addonVariant = findMultiConfigAddonVariant(
      apiProduct.multi_config_addon_sku
    );
    const includedVariant = findMultiConfigIncludedVariant(
      apiProduct.multi_config_included_sku
    );
    const pricingThreshold = apiProduct.multi_config_pricing_threshold ?? 2;
    const needsIncludedVariant = configSnapshots.some(
      (_, index) => index > 0 && index + 1 < pricingThreshold
    );
    const needsAddonVariant = configSnapshots.some(
      (_, index) => index > 0 && index + 1 >= pricingThreshold
    );

    if (needsIncludedVariant && !includedVariant) {
      return {
        success: false,
        error:
          'Included configuration unavailable - remove extra configs to continue'
      };
    }
    if (needsAddonVariant && !addonVariant) {
      return {
        success: false,
        error: 'Addon unavailable - remove extra configs to continue'
      };
    }

    const previewSvgByConfigId = await capturePreviewSvgPerConfig(
      configSnapshots,
      configId => `config-preview-${configId}`
    );

    // Promise.allSettled (see useSendMultiConfigSessionToCart) so a failure
    // names the specific configuration(s) affected. Only the customer-api
    // save is covered here - if any of these fail, nothing is touched on the
    // Shopify side at all.
    const results = await Promise.allSettled(
      configSnapshots.map(async (snapshot, index) => {
        const existingCartItemId = submittedCartItemsByConfigId[snapshot.id];
        const existingKey = editSessionShopifyKeysByConfigId[snapshot.id];

        // multi_config_session_id must be resent on every update, not just
        // create: CartItemsService.update() (customer-api) overwrites the
        // entire stored record with whatever's in this payload (merging in
        // only style_id/palette_id/partner_id from the previous version) -
        // omitting it here would silently drop it from storage, and the
        // next edit attempt (which reads it back via getApiCartItem to
        // decide whether to reconstruct the whole session) would then only
        // see this one configuration.
        const apiCartItemFields = {
          ...buildPrimaryApiCartItem(
            snapshot.properties,
            apiProduct,
            previewSvgByConfigId[snapshot.id]
          ),
          multi_config_session_id: editSessionMultiConfigSessionId
        };

        const cartItemId = existingCartItemId
          ? await updateApiCartItem(existingCartItemId, apiCartItemFields)
          : await sendApiCartItem(apiCartItemFields);

        const variantId = resolveConfigVariant(
          index,
          snapshot.properties,
          apiProduct,
          addonVariant,
          includedVariant
        );

        if (!variantId) {
          throw new Error(`No variant found for configuration ${index + 1}`);
        }

        const itemProperties = {
          ...buildCartItemProperties(snapshot.properties, {
            apiProduct,
            cartItemId,
            fonts,
            showIcons,
            showLabelShapes,
            materials,
            hideFontsAndFields,
            labelHasMultipleVariants
          }),
          _multi_config_session_id: editSessionMultiConfigSessionId,
          _multi_config_index: (index + 1).toString(),
          _multi_config_count: configSnapshots.length.toString()
        };

        return {
          configId: snapshot.id,
          cartItemId,
          existingKey,
          apiCartItemFields,
          shopifyLine: {
            id: variantId,
            quantity: 1,
            properties: itemProperties
          }
        };
      })
    );

    const failedConfigNumbers = results
      .map((result, index) => (result.status === 'rejected' ? index + 1 : null))
      .filter((n): n is number => n !== null);

    if (failedConfigNumbers.length > 0) {
      console.error(
        'error saving multi-config session edit',
        results
          .filter(
            (result): result is PromiseRejectedResult =>
              result.status === 'rejected'
          )
          .map(result => result.reason)
      );
      return {
        success: false,
        error: `Error saving configuration${
          failedConfigNumbers.length > 1 ? 's' : ''
        } ${failedConfigNumbers.join(', ')} - please try again`
      };
    }

    const fulfilledResults = results.filter(
      (result): result is (typeof results)[number] & { status: 'fulfilled' } =>
        result.status === 'fulfilled'
    );

    // Configurations that were part of the original session but aren't in
    // the current session anymore - deleted during this edit.
    const currentConfigIds = new Set(
      configSnapshots.map(snapshot => snapshot.id)
    );
    const removedConfigIds = Object.keys(
      editSessionShopifyKeysByConfigId
    ).filter(configId => !currentConfigIds.has(configId));
    const removedKeys = removedConfigIds.map(
      configId => editSessionShopifyKeysByConfigId[configId]
    );
    // A deleted configuration's own linked upsells are meaningless without
    // it (their _parent_item_id would point at nothing) - remove their
    // Shopify lines too. Their customer-api records are left as orphans,
    // same as the base configuration's own record - consistent with how
    // this codebase already handles unused cart_items (a scheduled cleanup
    // job, not an explicit delete endpoint).
    const removedUpsellKeys = removedConfigIds.flatMap(
      configId => editSessionUpsellsByConfigId[configId]?.map(u => u.key) || []
    );

    try {
      await Promise.all(
        [...removedKeys, ...removedUpsellKeys].map(key =>
          removeShopifyCartItem(key)
        )
      );

      const linesToAdd: Array<{
        id: number | undefined;
        quantity: number;
        properties: object;
      }> = [];
      const linesToUpdate: Array<{
        key: string;
        line: { id: number | undefined; quantity: number; properties: object };
      }> = [];

      for (const { value } of fulfilledResults) {
        const { existingKey, shopifyLine } = value;
        if (!existingKey) {
          linesToAdd.push(shopifyLine);
          continue;
        }

        const [existingVariantId] = existingKey.split(':');
        if (Number(existingVariantId) !== Number(shopifyLine.id)) {
          // The variant this configuration maps to changed (e.g. it moved
          // from an addon-priced position to the full-price position, or
          // vice versa) - Shopify can't change an existing line's variant
          // in place, so replace it like the single-item edit flow does.
          await removeShopifyCartItem(existingKey);
          linesToAdd.push(shopifyLine);
        } else {
          linesToUpdate.push({ key: existingKey, line: shopifyLine });
        }
      }

      await Promise.all(
        linesToUpdate.map(({ key, line }) => updateShopifyCartItem(key, line))
      );

      if (linesToAdd.length > 0) {
        const response = await addShopifyCartItems(linesToAdd);
        if (!response.ok) {
          const json = await response.json();
          console.error('error saving multi-config session edit', json);
          return { success: false, error: 'Error adding to cart' };
        }
      }

      // Keep each surviving configuration's linked upsells (if any) in sync
      // with its current personalization - matching how single-item editing
      // already keeps a regular product's upsells in sync with its base
      // item. Best-effort: an upsell sync failure doesn't fail the whole
      // save, since the configurations themselves already saved
      // successfully above.
      await Promise.all(
        fulfilledResults.map(({ value }) => {
          const linkedUpsells = editSessionUpsellsByConfigId[value.configId];
          if (!linkedUpsells?.length) return Promise.resolve();
          return syncLinkedUpsells(
            value.configId,
            linkedUpsells,
            value.apiCartItemFields,
            value.shopifyLine.properties
          ).catch(error => {
            console.error(
              `Error syncing upsells for configuration ${value.configId}:`,
              error
            );
          });
        })
      );
    } catch (error) {
      console.error('Error saving multi-config session edit:', error);
      return { success: false, error: 'Error adding to cart' };
    }

    dispatch(
      setSubmittedCartItems(
        Object.fromEntries(
          fulfilledResults.map(({ value }) => [
            value.configId,
            value.cartItemId
          ])
        )
      )
    );

    const primaryCartItemId = fulfilledResults[0]?.value.cartItemId;
    const productName = apiProduct.display_name || SHOPIFY_PRODUCT_NAME;
    return { success: true, productName, cartItemId: primaryCartItemId };
  }, [
    apiProduct,
    configSnapshots,
    submittedCartItemsByConfigId,
    editSessionShopifyKeysByConfigId,
    editSessionMultiConfigSessionId,
    editSessionUpsellsByConfigId,
    dispatch,
    fonts,
    showIcons,
    showLabelShapes,
    materials,
    hideFontsAndFields,
    labelHasMultipleVariants
  ]);

  return saveSessionEdit;
}

export function useAddUpsellToCart(
  upsell: UpsellProduct, // UpsellProduct type from upsells feature
  selectedMaterial: Material, // Material type
  baseProductCartItemId?: string,
  productTag?: string
) {
  const properties = useSelector(selectItemProperties);
  const { fonts } = useFontsAndFields();
  const { showIcons } = useIconsAndLabels();
  const { data: apiProduct } = useGetProductQuery(
    productTag || SHOPIFY_PRODUCT_TAG
  );

  const addUpsell = useCallback(async (): Promise<{
    success: boolean;
    error?: string;
  }> => {
    if (!apiProduct) {
      return { success: false, error: 'Product not loaded' };
    }

    try {
      // Build primary cart item for reference
      const primaryApiCartItem = buildPrimaryApiCartItem(
        properties,
        apiProduct
      );

      // Build upsell cart item
      const chosenUpsell = { upsell, material: selectedMaterial };
      const upsellApiCartItemData = buildUpsellApiCartItem(
        chosenUpsell,
        properties,
        primaryApiCartItem
      );

      // Send to API
      const upsellCartItemId = await sendApiCartItem(upsellApiCartItemData);

      // Build Shopify cart properties for upsell
      const upsellProperties: { [key: string]: string } = {};

      // Calculate label quantity
      const labelQuantity = upsell.label_slots.reduce(
        (acc: number, slot: LabelSlot) => {
          const { slot_options } = slot;
          const [option] = slot_options;
          return (acc += option?.quantity ?? 0);
        },
        0
      );

      upsellProperties.Labels = labelQuantity.toString();

      // Add text fields
      const { fields } = properties;
      const configuredTextFields = Object.values(fields);

      // Add Name or Full Name first
      const prioritizedDisplayNames = ['Name', 'Full Name'];
      const prioritizedTextField = configuredTextFields.find(field =>
        prioritizedDisplayNames.includes(field.display_name!)
      );

      if (prioritizedTextField) {
        assignTextFieldProperty(upsellProperties, prioritizedTextField);
      }

      // Add other text fields
      for (const field of configuredTextFields) {
        const isAlreadyAdded =
          prioritizedTextField &&
          field.display_name === prioritizedTextField.display_name;

        if (isAlreadyAdded) continue;

        assignTextFieldProperty(upsellProperties, field);
      }

      // Add style, color, font, icon
      const { style, palette, font, icon } = properties;

      upsellProperties.Style = style!.display_name;
      upsellProperties.Color = palette!.display_name;

      if (fonts?.length > 1 && font) {
        upsellProperties.Font = font.display_name;
      }

      if (showIcons && icon) {
        upsellProperties.Icon = icon.display_name;
      }

      // Add material
      if (selectedMaterial?.display_name) {
        upsellProperties['Clothing Type'] = selectedMaterial.display_name;
      }

      // Add system properties
      upsellProperties._order_system = 'NEW';
      upsellProperties._cart_item_id = upsellCartItemId;

      // Link upsell to base product
      if (baseProductCartItemId) {
        upsellProperties._parent_item_id = baseProductCartItemId;
      }

      // Add metadata IDs
      if (properties.style?.style_id)
        upsellProperties._style_id = properties.style.style_id;
      if (properties.palette?.palette_id)
        upsellProperties._palette_id = properties.palette.palette_id;
      if (properties.font?.font_id)
        upsellProperties._font_id = properties.font.font_id;
      if (properties.icon?.icon_id)
        upsellProperties._icon_id = properties.icon.icon_id;
      if (selectedMaterial?.material_id)
        upsellProperties._material_id = selectedMaterial.material_id;

      // Add to Shopify cart
      const response = await addShopifyCartItem({
        id: upsell.shopify_id!,
        quantity: 1,
        properties: upsellProperties
      });

      if (!response.ok) {
        const json = await response.json();
        console.error('error adding upsell to cart', json);
        return { success: false, error: 'Error adding upsell to cart' };
      }

      return { success: true };
    } catch (error) {
      console.error('Error adding upsell to cart:', error);
      return { success: false, error: 'Error adding upsell to cart' };
    }
  }, [
    upsell,
    selectedMaterial,
    properties,
    apiProduct,
    baseProductCartItemId,
    fonts?.length,
    showIcons
  ]);

  return addUpsell;
}

// Multi-config counterpart to useAddUpsellToCart: a chosen upsell is added
// once per configuration in the session (linked via _parent_item_id to that
// configuration's own cart item, from selectSubmittedCartItemsByConfigId -
// populated by useSendMultiConfigSessionToCart), each personalized with that
// configuration's own properties, rather than being added once for
// configuration #1 only.
export function useAddMultiConfigUpsellToCart(
  upsell: UpsellProduct,
  selectedMaterial: Material,
  productTag?: string
) {
  const configSnapshots = useRootSelector(selectAllConfigSnapshots);
  const submittedCartItemsByConfigId = useRootSelector(
    selectSubmittedCartItemsByConfigId
  );
  const { fonts } = useFontsAndFields();
  const { showIcons } = useIconsAndLabels();
  const { data: apiProduct } = useGetProductQuery(
    productTag || SHOPIFY_PRODUCT_TAG
  );

  const addUpsellToAllConfigs = useCallback(async (): Promise<{
    success: boolean;
    error?: string;
  }> => {
    if (!apiProduct) {
      return { success: false, error: 'Product not loaded' };
    }

    const previewSvgByConfigId = await capturePreviewSvgPerConfig(
      configSnapshots,
      configId => `config-preview-${configId}-upsell-${upsell.product_id}`
    );

    // See useSendMultiConfigSessionToCart's comment - Promise.allSettled so
    // a failure names the specific configuration(s) affected, but the
    // upsell submission stays all-or-nothing across configurations.
    const results = await Promise.allSettled(
      configSnapshots.map(async snapshot => {
        const { properties } = snapshot;
        const baseProductCartItemId = submittedCartItemsByConfigId[snapshot.id];

        const primaryApiCartItem = buildPrimaryApiCartItem(
          properties,
          apiProduct,
          previewSvgByConfigId[snapshot.id]
        );
        const chosenUpsell = { upsell, material: selectedMaterial };
        const upsellApiCartItemData = buildUpsellApiCartItem(
          chosenUpsell,
          properties,
          primaryApiCartItem,
          previewSvgByConfigId[snapshot.id]
        );

        const upsellCartItemId = await sendApiCartItem(upsellApiCartItemData);

        const upsellProperties: { [key: string]: string } = {};

        const labelQuantity = upsell.label_slots.reduce(
          (acc: number, slot: LabelSlot) => {
            const { slot_options } = slot;
            const [option] = slot_options;
            return (acc += option?.quantity ?? 0);
          },
          0
        );
        upsellProperties.Labels = labelQuantity.toString();

        const { fields } = properties;
        const configuredTextFields = Object.values(fields);
        const prioritizedDisplayNames = ['Name', 'Full Name'];
        const prioritizedTextField = configuredTextFields.find(field =>
          prioritizedDisplayNames.includes(field.display_name!)
        );
        if (prioritizedTextField) {
          assignTextFieldProperty(upsellProperties, prioritizedTextField);
        }
        for (const field of configuredTextFields) {
          const isAlreadyAdded =
            prioritizedTextField &&
            field.display_name === prioritizedTextField.display_name;
          if (isAlreadyAdded) continue;
          assignTextFieldProperty(upsellProperties, field);
        }

        const { style, palette, font, icon } = properties;
        upsellProperties.Style = style!.display_name;
        upsellProperties.Color = palette!.display_name;
        if (fonts?.length > 1 && font) {
          upsellProperties.Font = font.display_name;
        }
        if (showIcons && icon) {
          upsellProperties.Icon = icon.display_name;
        }
        if (selectedMaterial?.display_name) {
          upsellProperties['Clothing Type'] = selectedMaterial.display_name;
        }

        upsellProperties._order_system = 'NEW';
        upsellProperties._cart_item_id = upsellCartItemId;
        if (baseProductCartItemId) {
          upsellProperties._parent_item_id = baseProductCartItemId;
        }

        if (properties.style?.style_id)
          upsellProperties._style_id = properties.style.style_id;
        if (properties.palette?.palette_id)
          upsellProperties._palette_id = properties.palette.palette_id;
        if (properties.font?.font_id)
          upsellProperties._font_id = properties.font.font_id;
        if (properties.icon?.icon_id)
          upsellProperties._icon_id = properties.icon.icon_id;
        if (selectedMaterial?.material_id)
          upsellProperties._material_id = selectedMaterial.material_id;

        return {
          id: upsell.shopify_id!,
          quantity: 1,
          properties: upsellProperties
        };
      })
    );

    const failedConfigNumbers = results
      .map((result, index) => (result.status === 'rejected' ? index + 1 : null))
      .filter((n): n is number => n !== null);

    if (failedConfigNumbers.length > 0) {
      console.error(
        'error building multi-config upsell cart items',
        results
          .filter(
            (result): result is PromiseRejectedResult =>
              result.status === 'rejected'
          )
          .map(result => result.reason)
      );
      return {
        success: false,
        error: `Error adding upsell to configuration${
          failedConfigNumbers.length > 1 ? 's' : ''
        } ${failedConfigNumbers.join(', ')} - please try again`
      };
    }

    const shopifyLines = results
      .filter(
        (
          result
        ): result is (typeof results)[number] & { status: 'fulfilled' } =>
          result.status === 'fulfilled'
      )
      .map(({ value }) => value);

    const response = await addShopifyCartItems(shopifyLines);

    if (!response.ok) {
      const json = await response.json();
      console.error('error adding multi-config upsell to cart', json);
      return { success: false, error: 'Error adding upsell to cart' };
    }

    return { success: true };
  }, [
    apiProduct,
    configSnapshots,
    submittedCartItemsByConfigId,
    upsell,
    selectedMaterial,
    fonts?.length,
    showIcons
  ]);

  return addUpsellToAllConfigs;
}
